export interface RevocacionSolicitante {
  solicitudId?: number | string;
  primerNombre?: string;
  segundoNombre?: string;
  primerApellido?: string;
  segundoApellido?: string;
  correo?: string;
  celular?: string;
}

export interface MotivoRevocacion {
  motivoRevocacionId?: number;
  descCortaMotivo?: string;
  descripcionMotivo?: string;
}

export interface CertificadoRevocacion {
  organizacion?: string;
  nombreSolicitante?: string;
  username?: string;
  status?: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  serialDispositivo?: string;
  politicaCertificado?: string;
  cedula?: string;
  correoElectronicoSolicitante?: string;
  nit?: string;
  direccionFisica?: string;
  tipo?: string;
  usaDobleAutenticacion?: boolean;
  tokenRse?: string;
  motivoRevocacionId?: number;
  motivoRevocacion?: string;
  [key: string]: unknown;
}

export interface PreguntaReto {
  texto?: string;
  /** Opciones: el API usa `respuesta` (paridad Canal / validación identidad). */
  respuestas?: {
    respuesta?: string;
    texto?: string;
    seleccionada?: boolean;
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface PreguntasRetoResponse {
  preguntas?: PreguntaReto[];
  applicationId?: string;
}

/** Nombre corto para saludo (primer nombre + primer apellido). */
export function armarNombreSaludoRevocacion(s: RevocacionSolicitante | null | undefined): string {
  if (!s) {
    return '';
  }
  const corto = [s.primerNombre, s.primerApellido]
    .map((p) => String(p ?? '').trim())
    .filter(Boolean)
    .join(' ');
  if (corto) {
    return toTitleCase(corto);
  }
  const full = [s.primerNombre, s.segundoNombre, s.primerApellido, s.segundoApellido]
    .map((p) => String(p ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return toTitleCase(full);
}

export function nombresCompletos(s: RevocacionSolicitante): { nombres: string; apellidos: string } {
  const nombres = [s.primerNombre, s.segundoNombre]
    .map((p) => String(p ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const apellidos = [s.primerApellido, s.segundoApellido]
    .map((p) => String(p ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return { nombres, apellidos };
}

export function etiquetaEstadoCertificado(status: unknown): string {
  const s = String(status ?? '').trim();
  if (!s) {
    return '—';
  }
  return s.toUpperCase() === 'ACTIVO' ? 'Vigente' : s;
}

export function estaCertificadoActivo(item: { status?: string } | null | undefined): boolean {
  return String(item?.status ?? '').toUpperCase() === 'ACTIVO';
}

export function requiereTokenRse(item: CertificadoRevocacion | null | undefined): boolean {
  const tipo = String(item?.tipo ?? '').toUpperCase();
  return tipo.includes('TOKEN VIRTUAL') && !!item?.usaDobleAutenticacion;
}

/** Valida dd/mm/aaaa y que no sea posterior a hoy. */
export function fechaExpedicionInvalida(raw: unknown): boolean {
  const dato = String(raw ?? '').trim();
  if (!dato) {
    return true;
  }
  const valida =
    /^(?:(?:(?:0?[1-9]|1\d|2[0-8])[/](?:0?[1-9]|1[0-2])|(?:29|30)[/](?:0?[13-9]|1[0-2])|31[/](?:0?[13578]|1[02]))[/](?:0{2,3}[1-9]|0{1,2}[1-9]\d|0?[1-9]\d{2}|[1-9]\d{3})|29[/]0?2[/](?:\d{1,2}(?:0[48]|[2468][048]|[13579][26])|(?:0?[48]|[13579][26]|[2468][048])00))$/;
  if (!valida.test(dato)) {
    return true;
  }
  const [dd, mm, yyyy] = dato.split('/').map((p) => parseInt(p, 10));
  const fecha = new Date(yyyy, mm - 1, dd);
  const hoy = new Date();
  hoy.setHours(23, 59, 59, 999);
  return fecha.getTime() > hoy.getTime();
}

export function sanitizarFechaInput(raw: string): string {
  return String(raw ?? '')
    .replace(/[^\d/]/g, '')
    .slice(0, 10);
}

/** Convierte dd/mm/aaaa a yyyy-mm-dd (input type=date). */
export function fechaDdMmYyyyAIso(raw: unknown): string {
  const dato = String(raw ?? '').trim();
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dato)) {
    return '';
  }
  const [dd, mm, yyyy] = dato.split('/');
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

/** Convierte yyyy-mm-dd a dd/mm/aaaa. */
export function fechaIsoADdMmYyyy(raw: unknown): string {
  const dato = String(raw ?? '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dato);
  if (!m) {
    return '';
  }
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Hoy en yyyy-mm-dd (zona local). */
export function hoyIsoLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function toTitleCase(value: string): string {
  return value.toLowerCase().replace(/(^|\s)\S/g, (ch) => ch.toUpperCase());
}

export function parseMotivosResponse(res: unknown): MotivoRevocacion[] {
  if (Array.isArray(res)) {
    return res as MotivoRevocacion[];
  }
  return [];
}

export function parseCertificadosResponse(res: unknown): CertificadoRevocacion[] {
  if (Array.isArray(res)) {
    return res as CertificadoRevocacion[];
  }
  return [];
}
